import { useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceReportButton } from "@/components/VoiceReportButton";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import { pingNow } from "@/lib/tracking";

export function CantiereCloseForm({ job, onClosed }) {
  const team = [{ id: job.technician_id, name: `${job.technician_name} (responsabile)` }, ...(job.helpers || [])];
  const [summary, setSummary] = useState("");
  const [hours, setHours] = useState(Object.fromEntries(team.map((t) => [t.id, ""])));
  const [mats, setMats] = useState([]);
  const [signature, setSignature] = useState(null);
  const [signer, setSigner] = useState(job.site?.committente || "");
  const [busy, setBusy] = useState(false);
  const setMat = (i, k, v) => setMats((ms) => ms.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const total = team.reduce((s, t) => s + (Number(hours[t.id]) || 0), 0);

  const submit = async () => {
    if (summary.trim().length < 3) return toast.error("Descrivi il lavoro svolto nel cantiere");
    if (!total) return toast.error("Inserisci le ore della squadra");
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/close`, {
        summary: summary.trim(), hours: total, signature, signer_name: signature ? signer.trim() || null : null,
        team_hours: team.map((t) => ({ id: t.id, name: t.name.replace(" (responsabile)", ""), hours: Number(hours[t.id]) || 0 })),
        parts: mats.filter((x) => x.name.trim()).map((x) => ({ name: x.name.trim(), qty: Number(x.qty) || 1, unit_price: 0 })),
      });
      toast.success("Cantiere chiuso");
      pingNow("job_close", job.id);
      onClosed(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="cantiere-close-form">
      <div className="space-y-2"><div className="flex flex-wrap items-center justify-between gap-2"><Label>Lavoro svolto nel cantiere *</Label>
        <VoiceReportButton kind="cantiere" value={summary} onText={setSummary} testid="cantiere-close-voice" /></div>
        <Textarea rows={6} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="cantiere-close-summary" placeholder="Riepilogo complessivo dei lavori eseguiti" /></div>
      <div className="space-y-2">
        <Label>Ore della squadra (totali sul cantiere)</Label>
        {team.map((t) => (
          <div key={t.id} className="grid grid-cols-[1fr_96px] items-center gap-2" data-testid={`team-hours-row-${t.id}`}>
            <span className="text-sm">{t.name}</span>
            <Input type="number" min="0" step="0.5" inputMode="decimal" placeholder="ore" value={hours[t.id]} onChange={(e) => setHours((h) => ({ ...h, [t.id]: e.target.value }))} data-testid={`team-hours-input-${t.id}`} />
          </div>
        ))}
        <p className="text-xs text-slate-500" data-testid="team-hours-total">Totale squadra: {total} h</p>
      </div>
      <div className="space-y-2">
        <Label>Materiali utilizzati</Label>
        {mats.map((x, i) => (
          <div key={i} className="grid grid-cols-[1fr_72px_32px] gap-1.5" data-testid={`material-row-${i}`}>
            <Input placeholder="Descrizione" value={x.name} onChange={(e) => setMat(i, "name", e.target.value)} data-testid={`material-name-${i}`} />
            <Input type="number" min="1" inputMode="decimal" value={x.qty} onChange={(e) => setMat(i, "qty", e.target.value)} data-testid={`material-qty-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => setMats((ms) => ms.filter((_, j) => j !== i))} data-testid={`material-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setMats((ms) => [...ms, { name: "", qty: 1 }])} data-testid="material-add-button"><Plus className="h-4 w-4" /> Aggiungi materiale</Button>
      </div>
      <div className="space-y-2">
        <Label>Firma del committente o referente</Label>
        <Input value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Nome di chi firma" data-testid="cantiere-signer-input" />
        <SignaturePad onChange={setSignature} />
      </div>
      <Button onClick={submit} disabled={busy} className="h-12 w-full rounded-full" data-testid="cantiere-close-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Chiudi cantiere e genera rapporto
      </Button>
    </div>
  );
}
